import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApi } from 'contexts/api-context'
import { convertKeysToCamelcase } from 'utils/object-utils'

import type { Stream, StreamConfig, UseQueryOmissions } from 'types'
import type { UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'

type GetCustomerStreamProps = {
  customerId: string
  startDate?: string
  endDate?: string
  includeParticipatingMatches?: boolean
}

type GetCustomerStreamParams = {
  startdate?: string
  enddate?: string
  include_participating_matches?: string
  include_configs?: string
  include_destinations?: string
}

const buildGetStreamsUrl = (
  customerId?: string,
  startDate?: string,
  endDate?: string,
  includeParticipatingMatches = false
) => {
  const params: GetCustomerStreamParams = { include_configs: 'true', include_destinations: 'true' }
  if (startDate) params.startdate = startDate
  if (endDate) params.enddate = endDate
  if (includeParticipatingMatches) params.include_participating_matches = 'true'

  let url = `/streams/cust/${customerId}`
  if (params) url += `?${new URLSearchParams(params)}`

  return url
}

export const useGetCustomerStreams = (
  props: GetCustomerStreamProps & { alt?: string },
  queryProps?: Omit<UseQueryOptions<Stream[], Error>, UseQueryOmissions>
): UseQueryResult<Stream[], Error> => {
  const { customerId, startDate, endDate, includeParticipatingMatches } = props
  const { enabled, ...rest } = queryProps ?? {}
  const api = useApi()
  const results = useQuery<Stream[], Error>({
    queryKey: ['streams', props],
    enabled: !!customerId && enabled !== false,
    queryFn: async () => {
      const res = await api.get(buildGetStreamsUrl(customerId, startDate, endDate, includeParticipatingMatches))
      return res.streams.map((stream: Stream) => {
        const configJson = stream.configJson ? JSON.parse(stream.configJson) : undefined
        return {
          ...stream,
          ...(configJson && { configJson: undefined, streamConfig: convertKeysToCamelcase(configJson) }),
        }
      })
    },
    ...rest,
  })
  const filteredData = results.data?.filter(stream => !stream.status || ![101, 102].includes(stream.status)) ?? []
  return { ...results, data: filteredData } as UseQueryResult<Stream[], Error>
}

export const useGetStream = (
  streamGuid?: string,
  props?: Omit<UseQueryOptions<Stream, Error>, UseQueryOmissions>
): UseQueryResult<Stream, Error> => {
  const { enabled, ...rest } = props ?? {}
  const api = useApi()
  return useQuery<Stream, Error>({
    queryKey: ['stream', { streamGuid }],
    queryFn: () => api.get(`/stream/${streamGuid}`),
    enabled: !!streamGuid && enabled !== false,
    ...rest,
  })
}

type PutCustomerStreamProps = {
  streamConfig: StreamConfig
}

export type PutCustomerStreamResponse = {
  status?: number
  statusDesc?: string
  streamGuid?: string
  httpResponseCode: number
  httpStatusCode: number
  outputMessage?: string | null
  data?: StreamConfig
}

export const useUpdateStream = (
  options?: Omit<UseMutationOptions<PutCustomerStreamResponse, Error, PutCustomerStreamProps>, 'mutationFn'>
): UseMutationResult<PutCustomerStreamResponse, Error, PutCustomerStreamProps> => {
  const queryClient = useQueryClient()
  const api = useApi()
  return useMutation({
    ...options,
    mutationFn: vars => api.putStream(vars.streamConfig),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries(['streams'])
      queryClient.invalidateQueries(['stream', { streamGuid: vars.streamConfig.streamGuid }])
    },
  })
}

export const useDeleteCustomerStream = (
  streamGuid: string,
  options?: Omit<UseMutationOptions<void, Error, void>, 'mutationFn'>
): UseMutationResult<void, Error, void> => {
  const queryClient = useQueryClient()
  const api = useApi()
  return useMutation({
    ...options,
    mutationFn: () => api.del(`control/${streamGuid}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['streams'])
    },
  })
}

type PostCustomerStreamTerminateProps = {
  streamGuid: string
}

export const useTerminateStream = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: Omit<UseMutationOptions<any, Error, PostCustomerStreamTerminateProps>, 'mutationFn'>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): UseMutationResult<any, Error, PostCustomerStreamTerminateProps> => {
  const queryClient = useQueryClient()
  const api = useApi()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useMutation<any, Error, PostCustomerStreamTerminateProps>({
    ...options,
    mutationFn: vars => api.post(`/control/${vars.streamGuid}/terminate`),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries(['stream', vars.streamGuid])
    },
  })
}

type PostOutputPropertiesChangeProps = {
  streamConfig: StreamConfig
}

export const useUpdateOutputProperties = (
  options?: Omit<UseMutationOptions<boolean, Error, PostOutputPropertiesChangeProps>, 'mutationFn'>
) => {
  const api = useApi()
  return useMutation<boolean, Error, PostOutputPropertiesChangeProps>({
    ...options,
    mutationFn: vars => api.post('/control/output', { body: JSON.stringify(vars.streamConfig) }),
  })
}

export const useStartStream = (
  options?: Omit<UseMutationOptions<boolean, Error, PostOutputPropertiesChangeProps>, 'mutationFn'>
) => useUpdateOutputProperties(options)
